import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, patientService } from '@/services/api';
import { translateApiMessage } from '@/lib/apiErrorMessages';

const AuthContext = createContext(null);
const ACCOUNT_PROFILE_OVERRIDES_KEY = 'accountProfileOverrides';

// OTP verification states
export const OTP_STATE = {
  NONE: 'none',
  LOGIN_REQUIRED: 'login_required',
  REGISTER_REQUIRED: 'register_required',
};

const normalizeRoleName = (value) => (value || '').toString().trim().toLowerCase();

const extractApiErrorMessage = (error, fallback) => translateApiMessage(error, fallback);

const extractApiPayloadMessage = (payload, fallback) => translateApiMessage(payload, fallback);

const readObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const extractAuthPayload = (responseData) => {
  const response = readObject(responseData);
  const payload = Object.keys(readObject(response.data)).length > 0
    ? readObject(response.data)
    : response;
  const userData = Object.keys(readObject(payload.user)).length > 0
    ? readObject(payload.user)
    : payload;
  const token =
    payload.api_token ||
    payload.token ||
    response.api_token ||
    response.token ||
    userData.api_token ||
    userData.token ||
    null;

  return { payload, userData, token };
};

const resolveUserType = (userData, fallback = 'patient') => {
  const partnerType = normalizeRoleName(userData?.partner_type);
  if (partnerType) {
    if (partnerType === 'client') return 'patient';
    return partnerType;
  }

  const explicitUserType = normalizeRoleName(userData?.user_type);
  if (explicitUserType) {
    if (explicitUserType === 'client') return 'patient';
    return explicitUserType;
  }

  const roleNames = Array.isArray(userData?.roles)
    ? userData.roles.map((role) => normalizeRoleName(role?.name))
    : [];

  if (roleNames.includes('doctor')) return 'doctor';
  if (roleNames.includes('clinic') || roleNames.includes('clinic_owner')) return 'clinic';
  if (roleNames.includes('hospital')) return 'hospital';
  if (roleNames.includes('patient') || roleNames.includes('client')) return 'patient';

  const normalizedFallback = normalizeRoleName(fallback);
  if (normalizedFallback === 'doctor') return 'doctor';
  if (normalizedFallback === 'clinic' || normalizedFallback === 'clinic_owner') return 'clinic';
  if (normalizedFallback === 'hospital') return 'hospital';
  return 'patient';
};

const buildDisplayName = (firstName, lastName, fallback = '') => {
  const fullName = [firstName, lastName]
    .map((value) => (value || '').toString().trim())
    .filter(Boolean)
    .join(' ');

  return fullName || fallback;
};

const splitDisplayName = (value = '') => {
  const normalized = (value || '').toString().trim().replace(/\s+/g, ' ');
  if (!normalized) {
    return { firstName: '', lastName: '' };
  }

  const [firstName, ...rest] = normalized.split(' ');
  return {
    firstName: firstName || '',
    lastName: rest.join(' '),
  };
};

const readAccountProfileOverrides = () => {
  try {
    const raw = localStorage.getItem(ACCOUNT_PROFILE_OVERRIDES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

const saveAccountProfileOverride = (userId, override) => {
  if (!userId) {
    return;
  }

  const overrides = readAccountProfileOverrides();
  overrides[String(userId)] = {
    ...readObject(overrides[String(userId)]),
    ...override,
  };
  localStorage.setItem(ACCOUNT_PROFILE_OVERRIDES_KEY, JSON.stringify(overrides));
};

const applyAccountProfileOverride = (userData) => {
  if (!userData?.id) {
    return userData;
  }

  const override = readObject(readAccountProfileOverrides()[String(userData.id)]);
  if (Object.keys(override).length === 0) {
    return userData;
  }

  const firstName = (override.first_name ?? userData.first_name ?? '').toString().trim();
  const lastName = (override.last_name ?? userData.last_name ?? '').toString().trim();
  const name = (override.name ?? buildDisplayName(firstName, lastName, userData.name || '')).toString().trim();

  return {
    ...userData,
    first_name: firstName,
    last_name: lastName,
    name: name || userData.name,
  };
};

const normalizeUserProfile = (userData, fallbackType = 'patient') => {
  if (!userData) {
    return null;
  }

  const fallbackName =
    userData?.name ||
    userData?.display_name ||
    userData?.internal_name ||
    '';
  const derivedNames = splitDisplayName(fallbackName);
  const firstName = (userData?.first_name || derivedNames.firstName || '').toString().trim();
  const lastName = (userData?.last_name || derivedNames.lastName || '').toString().trim();
  const avatar =
    userData?.avatar ||
    userData?.image ||
    userData?.profile_image ||
    userData?.photo ||
    null;
  const resolvedType = resolveUserType(
    userData,
    userData?.user_type || userData?.partner_type || fallbackType
  );

  return {
    ...userData,
    first_name: firstName,
    last_name: lastName,
    name: buildDisplayName(firstName, lastName, fallbackName),
    image: userData?.image || avatar,
    avatar,
    user_type: resolvedType,
    partner_type: userData?.partner_type || resolvedType,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // OTP verification state
  const [otpState, setOtpState] = useState(OTP_STATE.NONE);
  const [pendingAuth, setPendingAuth] = useState(null); // Stores phone/email for OTP verification

  const syncUserState = (nextUser) => {
    if (!nextUser) {
      localStorage.removeItem('user');
      setUser(null);
      return;
    }

    const normalizedUser = applyAccountProfileOverride(
      normalizeUserProfile(
        nextUser,
        nextUser?.user_type || user?.user_type || 'patient'
      )
    );

    localStorage.setItem('user', JSON.stringify(normalizedUser));
    setUser(normalizedUser);
  };

  const updateUser = (updates, options = {}) => {
    setUser((currentUser) => {
      if (!currentUser) {
        return currentUser;
      }

      const nextFirstName = updates?.first_name ?? currentUser.first_name ?? '';
      const nextLastName = updates?.last_name ?? currentUser.last_name ?? '';
      const nextEmail = updates?.email ?? currentUser.email ?? '';
      const nextPhoneNumber = updates?.phone_number ?? updates?.phone ?? currentUser.phone_number ?? currentUser.phone ?? '';
      const nextName = updates?.name ?? buildDisplayName(nextFirstName, nextLastName, currentUser.name || '');

      const mergedUser = {
        ...currentUser,
        ...updates,
        first_name: nextFirstName,
        last_name: nextLastName,
        email: nextEmail,
        phone_number: nextPhoneNumber,
        phone: nextPhoneNumber,
        name: nextName,
      };

      if (
        currentUser.patient &&
        typeof currentUser.patient === 'object' &&
        updates?.patient &&
        typeof updates.patient === 'object'
      ) {
        mergedUser.patient = {
          ...currentUser.patient,
          ...updates.patient,
        };
      }

      if (options.persistAccountProfile) {
        saveAccountProfileOverride(currentUser.id, {
          first_name: nextFirstName,
          last_name: nextLastName,
          name: nextName,
        });
      }

      const normalizedUser = applyAccountProfileOverride(
        normalizeUserProfile(
          mergedUser,
          mergedUser?.user_type || currentUser?.user_type || 'patient'
        )
      );

      localStorage.setItem('user', JSON.stringify(normalizedUser));
      return normalizedUser;
    });
  };

  useEffect(() => {
    // Check for stored auth on mount
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    
    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const normalizedUser = normalizeUserProfile(parsedUser, parsedUser?.user_type || 'patient');
        syncUserState(normalizedUser);
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  // Login function - matches Flutter app behavior
  // Returns: { success, needsOtp, error }
  const login = async (phoneOrEmail, password, userType = 'patient') => {
    try {
      // Format phone number if it looks like a phone
      let formattedPhone = phoneOrEmail;
      if (/^\d+$/.test(phoneOrEmail.replace(/[\s\-\+]/g, ''))) {
        // It's a phone number - ensure it has country code
        formattedPhone = phoneOrEmail.startsWith('+') ? phoneOrEmail : `+963${phoneOrEmail}`;
      }

      // Use the appropriate login method based on user type
      // Patient: POST /api/login (multipart/form-data)
      // Doctor: POST /api/doctor/login (JSON body)
      const response = await authService.login(formattedPhone, password, userType);
      
      if (response.data.success) {
        const userData = response.data.data || response.data;
        const resolvedUserType = resolveUserType(userData, userType);
        
        // Check if user is trying to login as wrong type
        if (userType === 'patient' && resolvedUserType === 'doctor') {
          return { success: false, error: 'You are not a patient' };
        }
        if (userType === 'doctor' && resolvedUserType !== 'doctor') {
          return { success: false, error: 'You are not a doctor' };
        }
        
        // Store auth data
        const token = userData.api_token || userData.token;
        localStorage.setItem('token', token);
        
        // For patient users, try to fetch their patient profile
        let patientId = null;
        if (resolvedUserType === 'patient' && userData.id) {
          try {
            // Pass the user ID directly to fetch their patients
            const patientsResponse = await patientService.getByUserId(userData.id);
            const patients = patientsResponse.data?.data || patientsResponse.data || [];
            // Find the default patient or use the first one
            const defaultPatient = patients.find(p => p.is_default) || patients[0];
            if (defaultPatient) {
              patientId = defaultPatient.id;
              console.log('Found patient profile:', patientId, 'for user:', userData.id);
            } else {
              console.log('No patient profile found for user:', userData.id);
            }
          } catch (patientError) {
            console.log('Could not fetch patient profile:', patientError);
            // Not critical - user can still login, but won't be able to book
          }
        }
        
        const fullUserData = {
          ...userData,
          user_type: resolvedUserType,
          partner_type: userData.partner_type || resolvedUserType,
          patient_id: patientId,
        };
        syncUserState(fullUserData);
        setIsAuthenticated(true);
        setOtpState(OTP_STATE.NONE);
        
        return { success: true, userType: resolvedUserType };
      } else {
        return { success: false, error: extractApiPayloadMessage(response.data, 'Login failed') };
      }
    } catch (error) {
      const status = error.response?.status;
      const message = extractApiErrorMessage(error, 'Login failed');
      
      // Status 450 or 452: OTP verification required (same as Flutter)
      if (status === 450 || status === 452) {
        setPendingAuth({ phoneOrEmail, password, userType });
        setOtpState(OTP_STATE.LOGIN_REQUIRED);
        return { success: false, needsOtp: true, error: message };
      }
      
      // DEMO MODE: If API fails due to CORS/network error, or for testing purposes
      // Check for demo credentials or network errors
      const isDemoLogin = phoneOrEmail === 'demo' || phoneOrEmail === '+963demo' || 
                          phoneOrEmail.includes('demo@') || password === 'demo123';
      const isNetworkError = !error.response || error.code === 'ERR_NETWORK';
      
      if (isNetworkError || isDemoLogin) {
        console.log('Demo mode: Creating mock user session');
        const mockUser = {
          id: 999,
          first_name: userType === 'doctor' ? 'Ahmed' : 'Mohammed',
          last_name: userType === 'doctor' ? 'Hassan' : 'Ali',
          email: phoneOrEmail.includes('@') ? phoneOrEmail : 'user@dactrha.com',
          phone: phoneOrEmail.includes('@') ? '+963999123456' : phoneOrEmail,
          api_token: 'mock_token_' + Date.now(),
          roles: userType === 'doctor' 
            ? [{ id: 2, name: 'doctor' }] 
            : [{ id: 1, name: 'client' }],
          avatar: null,
          user_type: userType,
        };
        
        localStorage.setItem('token', mockUser.api_token);
        syncUserState(mockUser);
        setIsAuthenticated(true);
        setOtpState(OTP_STATE.NONE);
        
        return { success: true, isDemo: true };
      }
      
      return { success: false, error: message };
    }
  };

  // Register function - matches Flutter app behavior
  // Returns: { success, needsOtp, error }
  const register = async (data) => {
    try {
      // Format phone number
      let formattedPhone = data.phone;
      if (formattedPhone && !formattedPhone.startsWith('+')) {
        formattedPhone = `+${data.countryCode || '963'}${formattedPhone}`;
      }

      const response = await authService.register({
        name: data.name,
        email: data.email,
        phoneNumber: formattedPhone,
        password: data.password,
        cityId: data.cityId,
        deviceToken: data.deviceToken || '',
      });
      
      if (response.data.success) {
        // Registration successful - OTP sent
        setPendingAuth({ phoneOrEmail: formattedPhone, password: data.password });
        setOtpState(OTP_STATE.REGISTER_REQUIRED);
        return { success: true, needsOtp: true };
      } else {
        return { success: false, error: extractApiPayloadMessage(response.data, 'Registration failed') };
      }
    } catch (error) {
      const status = error.response?.status;
      const message = extractApiErrorMessage(error, 'Registration failed');
      
      // Status 402: OTP already sent (same as Flutter)
      if (status === 402) {
        let formattedPhone = data.phone;
        if (formattedPhone && !formattedPhone.startsWith('+')) {
          formattedPhone = `+${data.countryCode || '963'}${formattedPhone}`;
        }
        setPendingAuth({ phoneOrEmail: formattedPhone, password: data.password });
        setOtpState(OTP_STATE.REGISTER_REQUIRED);
        return { success: false, needsOtp: true, error: message };
      }
      
      return { success: false, error: message };
    }
  };

  // Verify OTP - matches Flutter app behavior
  const verifyOtp = async (otpCode) => {
    if (!pendingAuth) {
      return { success: false, error: 'No pending verification' };
    }

    try {
      const response = await authService.verifyOtp(pendingAuth.phoneOrEmail, otpCode);
      
      if (response.data.success) {
        const userData = response.data.data || response.data;
        const resolvedUserType = resolveUserType(userData, pendingAuth.userType || 'patient');
        const token = userData.api_token || userData.token;
        let patientId = null;

        if (resolvedUserType === 'patient' && userData.id) {
          try {
            const patientsResponse = await patientService.getByUserId(userData.id);
            const patients = patientsResponse.data?.data || patientsResponse.data || [];
            const defaultPatient = patients.find(p => p.is_default) || patients[0];
            if (defaultPatient) {
              patientId = defaultPatient.id;
            }
          } catch (_) {
            patientId = null;
          }
        }

        const fullUserData = {
          ...userData,
          user_type: resolvedUserType,
          partner_type: userData.partner_type || resolvedUserType,
          patient_id: patientId,
        };
        
        // Store auth data
        if (token) {
          localStorage.setItem('token', token);
        }
        syncUserState(fullUserData);
        setIsAuthenticated(true);
        setOtpState(OTP_STATE.NONE);
        setPendingAuth(null);
        
        return {
          success: true,
          isNewUser: otpState === OTP_STATE.REGISTER_REQUIRED,
          userType: resolvedUserType,
        };
      } else {
        return { success: false, error: extractApiPayloadMessage(response.data, 'Invalid OTP code') };
      }
    } catch (error) {
      return { success: false, error: extractApiErrorMessage(error, 'OTP verification failed') };
    }
  };

  // Resend OTP
  const resendOtp = async () => {
    if (!pendingAuth) {
      return { success: false, error: 'No pending verification' };
    }

    try {
      await authService.sendOtp(pendingAuth.phoneOrEmail);
      return { success: true };
    } catch (error) {
      return { success: false, error: extractApiErrorMessage(error, 'Failed to resend OTP') };
    }
  };

  // Cancel OTP verification
  const cancelOtpVerification = () => {
    setOtpState(OTP_STATE.NONE);
    setPendingAuth(null);
  };

  const completePasswordReset = async ({
    phoneOrEmail,
    otpCode,
    newPassword,
    userType = 'patient',
  }) => {
    try {
      const response = await authService.resetPasswordWithOtp(
        phoneOrEmail,
        otpCode,
        newPassword,
        userType
      );

      const { payload, userData, token } = extractAuthPayload(response.data);
      const hasAuthPayload = Boolean(
        token ||
        userData?.id ||
        payload?.id ||
        response.data?.success === true
      );

      if (!hasAuthPayload) {
        return {
          success: false,
          error: extractApiPayloadMessage(response.data, 'Failed to reset password'),
        };
      }

      const resolvedUserType = resolveUserType(userData, userType);

      if (token) {
        localStorage.setItem('token', token);
      }

      let patientId = null;
      if (resolvedUserType === 'patient' && userData?.id && token) {
        try {
          const patientsResponse = await patientService.getByUserId(userData.id);
          const patients = patientsResponse.data?.data || patientsResponse.data || [];
          const defaultPatient = patients.find((p) => p.is_default) || patients[0];
          if (defaultPatient) {
            patientId = defaultPatient.id;
          }
        } catch (_) {
          patientId = null;
        }
      }

      if (token || userData?.id || payload?.id) {
        syncUserState({
          ...userData,
          user_type: resolvedUserType,
          partner_type: userData.partner_type || resolvedUserType,
          patient_id: patientId,
        });
        setIsAuthenticated(true);
      }

      setOtpState(OTP_STATE.NONE);
      setPendingAuth(null);

      return {
        success: true,
        userType: resolvedUserType,
        needsLogin: !token && !userData?.id && !payload?.id,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiErrorMessage(error, 'Failed to reset password'),
      };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      setOtpState(OTP_STATE.NONE);
      setPendingAuth(null);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    otpState,
    pendingAuth,
    login,
    register,
    verifyOtp,
    resendOtp,
    cancelOtpVerification,
    completePasswordReset,
    updateUser,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
